import React from "react";
import { Loader2, AlertCircle } from "lucide-react";

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; loading?: boolean };

const VARIANTS = {
  primary: "bg-primary text-white hover:bg-primary-dark border border-transparent",
  secondary: "bg-surface text-primary border border-primary hover:bg-primary-light",
  ghost: "bg-transparent text-primary border border-transparent hover:bg-primary-light",
  danger: "bg-danger text-white border border-transparent hover:opacity-90",
};

export function Button({ variant = "primary", loading, className = "", children, disabled, ...rest }: BtnProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 text-base font-medium transition-[background-color,transform,opacity] duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${className}`}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function Card({ className = "", children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={`kd-card p-5 sm:p-6 ${className}`}>{children}</div>;
}

export function Field({ label, error, hint, required, children, htmlFor }: { label: string; error?: string; hint?: string; required?: boolean; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="kd-label">
        {label}{required && <span className="text-danger" aria-hidden> *</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-sm text-text-muted">{hint}</p>}
      {error && <p className="mt-1 flex items-center gap-1 text-sm text-danger" role="alert"><AlertCircle className="h-4 w-4" aria-hidden />{error}</p>}
    </div>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className = "", ...rest }, ref) => <input ref={ref} {...rest} className={`kd-input ${className}`} />
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  (props, ref) => <textarea ref={ref} rows={4} {...props} className={`kd-input ${props.className || ""}`} />,
);

export function Select({ options, placeholder, className = "", ...rest }: React.SelectHTMLAttributes<HTMLSelectElement> & { options: Record<string, string> | [string, string][]; placeholder?: string }) {
  const entries = Array.isArray(options) ? options : Object.entries(options);
  return (
    <select {...rest} className={`kd-input pr-8 ${className}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {entries.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

export function Checkbox({ label, checked, onChange, testid, error }: { label: React.ReactNode; checked: boolean; onChange: (v: boolean) => void; testid?: string; error?: boolean }) {
  return (
    <label className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 ${error ? "border-danger" : "border-border"} bg-surface`}>
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--kd-primary)]" />
      <span className="text-base">{label}</span>
    </label>
  );
}

export function ErrorBox({ children, testid }: { children: React.ReactNode; testid?: string }) {
  if (!children) return null;
  return (
    <div role="alert" data-testid={testid} className="mb-4 flex items-start gap-2 rounded-lg border border-danger bg-danger-bg p-3 text-sm text-danger">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

export function Spinner({ label = "Wird geladen …" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-text-muted" role="status">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> {label}
    </div>
  );
}

export function Dl({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt className="text-sm text-text-muted">{k}</dt>
          <dd className="break-words text-base">{v || "–"}</dd>
        </div>
      ))}
    </dl>
  );
}
